import { addTimelineEvent, performanceMeasureEnd, performanceMeasureStart } from './dev_tools';
import { FuncArrWithMeta } from './func_arr';
import { ENV } from './global_options';
import { isComponent, isShadowRoot } from './type_symbol';
/**
 * Event bubbling control
 */
export var EventBubbleStatus;
(function (EventBubbleStatus) {
    EventBubbleStatus[EventBubbleStatus["Normal"] = 0] = "Normal";
    EventBubbleStatus[EventBubbleStatus["NoDefault"] = 1] = "NoDefault";
})(EventBubbleStatus || (EventBubbleStatus = {}));
let relativeTimeStamp = Date.now();
let prevTimeStamp = relativeTimeStamp;
const getCurrentTimeStamp = () => {
    const ts = Date.now();
    if (ts < prevTimeStamp) {
        relativeTimeStamp += ts - prevTimeStamp;
    }
    prevTimeStamp = ts;
    return ts - relativeTimeStamp;
};
export var MutLevel;
(function (MutLevel) {
    MutLevel[MutLevel["None"] = 0] = "None";
    MutLevel[MutLevel["Mut"] = 1] = "Mut";
    MutLevel[MutLevel["Final"] = 2] = "Final";
})(MutLevel || (MutLevel = {}));
export var FinalChanged;
(function (FinalChanged) {
    FinalChanged[FinalChanged["NotChanged"] = 0] = "NotChanged";
    FinalChanged[FinalChanged["Failed"] = 1] = "Failed";
    FinalChanged[FinalChanged["None"] = 2] = "None";
    FinalChanged[FinalChanged["Final"] = 3] = "Final";
    FinalChanged[FinalChanged["Mut"] = 4] = "Mut";
})(FinalChanged || (FinalChanged = {}));
export var EventPhase;
(function (EventPhase) {
    EventPhase[EventPhase["None"] = 0] = "None";
    EventPhase[EventPhase["CapturingPhase"] = 1] = "CapturingPhase";
    EventPhase[EventPhase["AtTarget"] = 2] = "AtTarget";
    EventPhase[EventPhase["BubblingPhase"] = 3] = "BubblingPhase";
})(EventPhase || (EventPhase = {}));
/** The target of an event */
export class EventTarget {
    constructor() {
        this.listeners = Object.create(null);
        this.captureListeners = null;
    }
    addListener(name, func, options = {}) {
        // eslint-disable-next-line no-nested-ternary
        const mutLevel = options.final ? 2 /* MutLevel.Final */ : options.mutated ? 1 /* MutLevel.Mut */ : 0 /* MutLevel.None */;
        let listeners;
        if (options.capture || options.useCapture) {
            if (this.captureListeners) {
                listeners = this.captureListeners;
            }
            else {
                listeners = Object.create(null);
                this.captureListeners = listeners;
            }
        }
        else {
            listeners = this.listeners;
        }
        let efa;
        let initialized;
        if (listeners[name]) {
            initialized = true;
            efa = listeners[name];
        }
        else {
            initialized = false;
            efa = listeners[name] = {
                mutCount: 0,
                finalCount: 0,
                funcArr: new FuncArrWithMeta('listener'),
            };
        }
        efa.funcArr.add(func, mutLevel);
        if (mutLevel === 2 /* MutLevel.Final */) {
            efa.finalCount += 1;
            return initialized && efa.finalCount !== 1 ? 0 /* FinalChanged.NotChanged */ : 3 /* FinalChanged.Final */;
        }
        if (mutLevel === 1 /* MutLevel.Mut */) {
            efa.mutCount += 1;
            return initialized && !(efa.mutCount === 1 && efa.finalCount === 0)
                ? 0 /* FinalChanged.NotChanged */
                : 4 /* FinalChanged.Mut */;
        }
        return initialized ? 0 /* FinalChanged.NotChanged */ : 2 /* FinalChanged.None */;
    }
    removeListener(name, func, options = {}) {
        const listeners = options.capture || options.useCapture ? this.captureListeners : this.listeners;
        if (!listeners)
            return 1 /* FinalChanged.Failed */;
        const efa = listeners[name];
        if (!efa)
            return 1 /* FinalChanged.Failed */;
        const mutLevel = efa.funcArr.remove(func);
        if (mutLevel === null)
            return 1 /* FinalChanged.Failed */;
        if (mutLevel === 2 /* MutLevel.Final */) {
            efa.finalCount -= 1;
            // eslint-disable-next-line no-nested-ternary
            return efa.finalCount !== 0
                ? 0 /* FinalChanged.NotChanged */
                : efa.mutCount > 0
                    ? 4 /* FinalChanged.Mut */
                    : 2 /* FinalChanged.None */;
        }
        if (mutLevel === 1 /* MutLevel.Mut */) {
            efa.mutCount -= 1;
            return efa.mutCount !== 0 || efa.finalCount !== 0
                ? 0 /* FinalChanged.NotChanged */
                : 2 /* FinalChanged.None */;
        }
        return 0 /* FinalChanged.NotChanged */;
    }
    getListeners() {
        const finalListeners = Object.create(null);
        const resolveListeners = (listeners, capture) => {
            const names = Object.keys(listeners);
            for (let i = 0; i < names.length; i += 1) {
                const name = names[i];
                const efa = listeners[name];
                if (!finalListeners[name])
                    finalListeners[name] = [];
                const funcArr = efa.funcArr.getArr();
                if (funcArr) {
                    for (let j = 0; j < funcArr.length; j += 1) {
                        const funcInfo = funcArr[j];
                        finalListeners[name].push({
                            listener: funcInfo.f,
                            mutated: funcInfo.data === 1 /* MutLevel.Mut */,
                            final: funcInfo.data === 2 /* MutLevel.Final */,
                            capture,
                            useCapture: capture,
                        });
                    }
                }
            }
        };
        resolveListeners(this.listeners, false);
        if (this.captureListeners) {
            resolveListeners(this.captureListeners, false);
        }
        return finalListeners;
    }
}
export class Event {
    constructor(name, detail, options = {}) {
        this.eventPhase = 0 /* EventPhase.None */;
        const ts = getCurrentTimeStamp();
        this._$eventName = this.type = name;
        this.timeStamp = ts;
        this.detail = detail;
        this.bubbles = options.bubbles || false;
        this.composed = options.composed || false;
        this._$capturePhase = options.capturePhase || false;
        this._$eventBubblingControl = {
            stopped: false,
            mutated: false,
            noDefault: false,
        };
        this._$originalEvent = options.originalEvent;
        this._$dispatched = false;
        this._$hasListener = false;
        this._$handleListenerReturn = options.handleListenerReturn;
        if (options.extraFields) {
            Object.assign(this, options.extraFields);
            this.extraFields = options.extraFields;
        }
    }
    getEventBubbleStatus() {
        if (this._$eventBubblingControl.noDefault)
            return 1 /* EventBubbleStatus.NoDefault */;
        return 0 /* EventBubbleStatus.Normal */;
    }
    wrapShadowedEvent(targetCaller, mark, currentTargetCaller) {
        const ret = Object.create(this);
        ret.target = targetCaller;
        ret.mark = mark;
        ret.currentTarget = currentTargetCaller;
        return ret;
    }
    getEventName() {
        return this._$eventName;
    }
    getOriginalEvent() {
        return this._$originalEvent;
    }
    preventDefault() {
        this._$eventBubblingControl.noDefault = true;
    }
    defaultPrevented() {
        return this._$eventBubblingControl.noDefault;
    }
    stopPropagation() {
        this._$eventBubblingControl.stopped = true;
    }
    propagationStopped() {
        return this._$eventBubblingControl.stopped;
    }
    hasListener() {
        return this._$hasListener;
    }
    markMutated() {
        this._$eventBubblingControl.mutated = true;
    }
    mutatedMarked() {
        return this._$eventBubblingControl.mutated;
    }
    listenerReturnHandler() {
        return this._$handleListenerReturn;
    }
    isCapturePhase() {
        return this.eventPhase === 1 /* EventPhase.CapturingPhase */;
    }
    callListener(currentTarget, mark, target, isCapture) {
        const evName = this._$eventName;
        const eventTarget = currentTarget._$eventTarget;
        if (!eventTarget)
            return;
        const listeners = isCapture ? eventTarget.captureListeners : eventTarget.listeners;
        if (!listeners)
            return;
        const efa = listeners[evName];
        if (!efa)
            return;
        const skipMut = this.mutatedMarked();
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const targetCaller = isComponent(target) ? target.getMethodCaller() || target : target;
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const currentTargetCaller = isComponent(currentTarget)
            ? currentTarget.getMethodCaller() || currentTarget
            : currentTarget;
        this._$hasListener || (this._$hasListener = efa.funcArr.hasFunc());
        const ev = this.wrapShadowedEvent(targetCaller, mark, currentTargetCaller);
        const ret = efa.funcArr.call(currentTargetCaller, [ev], (mulLevel) => !skipMut || mulLevel !== 1 /* MutLevel.Mut */, isComponent(target) ? target : undefined, this._$handleListenerReturn);
        if (ret === false || efa.finalCount > 0) {
            ev.stopPropagation();
            ev.preventDefault();
        }
        else if (efa.mutCount > 0) {
            ev.markMutated();
        }
    }
    dispatch(target, externalTarget) {
        if (this._$dispatched) {
            throw new Error('Event cannot be dispatched twice');
        }
        if (ENV.DEV) {
            addTimelineEvent(this._$eventName, { event: this });
            performanceMeasureStart('event.dispatch');
        }
        this._$dispatched = true;
        const crossShadow = this.composed;
        const bubbles = this.bubbles;
        const isExternalAtTarget = externalTarget && isComponent(target) && target._$external;
        const inExternalOnly = !crossShadow && isExternalAtTarget;
        const eventBubblingControl = this._$eventBubblingControl;
        const forEachBubblePath = (target, f) => {
            const recShadow = (target) => {
                let currentTarget = target;
                const mark = target.collectMarks();
                for (;;) {
                    if (f(currentTarget, target, mark) === false)
                        return null;
                    let next;
                    if (crossShadow) {
                        if (isShadowRoot(currentTarget))
                            return currentTarget.getHostNode();
                        if (currentTarget.containingSlot === null)
                            return null;
                        if (currentTarget.containingSlot) {
                            next = recShadow(currentTarget.containingSlot);
                        }
                        else {
                            next = currentTarget.parentNode;
                            while (next === null || next === void 0 ? void 0 : next._$inheritSlots) {
                                next = next.parentNode;
                            }
                        }
                    }
                    else {
                        next = currentTarget.parentNode;
                    }
                    if (!next)
                        return null;
                    currentTarget = next;
                }
            };
            let cur = target;
            for (;;) {
                const next = recShadow(cur);
                if (!next)
                    break;
                cur = next;
            }
        };
        // capture phase
        if (this._$capturePhase && !eventBubblingControl.stopped && !inExternalOnly) {
            this.eventPhase = 1 /* EventPhase.CapturingPhase */;
            const bubblingPath = [];
            forEachBubblePath(target, (currentTarget, target, mark) => {
                bubblingPath.push([currentTarget, target, mark]);
            });
            for (let i = bubblingPath.length - 1; i >= 0; i -= 1) {
                const [currentTarget, target, mark] = bubblingPath[i];
                if (currentTarget._$eventTarget) {
                    this.callListener(currentTarget, mark, target, true);
                    if (eventBubblingControl.stopped)
                        break;
                }
            }
        }
        this.eventPhase = 2 /* EventPhase.AtTarget */;
        // bubble phase in external component
        if (!eventBubblingControl.stopped && externalTarget) {
            if (isComponent(target) && target._$external) {
                ;
                target.shadowRoot.handleEvent(externalTarget, this);
            }
        }
        // bubble phase
        if (!eventBubblingControl.stopped && !inExternalOnly) {
            let atTarget = true;
            forEachBubblePath(target, (currentTarget, target, mark) => {
                if (!atTarget && isComponent(currentTarget) && currentTarget._$external) {
                    const sr = currentTarget.shadowRoot;
                    sr.handleEvent(sr.slot, this);
                }
                atTarget = false;
                this.eventPhase = 3 /* EventPhase.BubblingPhase */;
                if (currentTarget._$eventTarget && !eventBubblingControl.stopped) {
                    this.callListener(currentTarget, mark, target, false);
                }
                return bubbles && !eventBubblingControl.stopped;
            });
        }
        this.eventPhase = 0 /* EventPhase.None */;
        if (ENV.DEV)
            performanceMeasureEnd();
        return this.getEventBubbleStatus();
    }
    static dispatchEvent(target, event) {
        return event.dispatch(target);
    }
    static dispatchExternalEvent(element, target, event) {
        return event.dispatch(element, target);
    }
    static triggerEvent(target, name, detail, options) {
        const ev = new Event(name, detail, options);
        return Event.dispatchEvent(target, ev);
    }
    static triggerExternalEvent(element, target, name, detail, options) {
        const ev = new Event(name, detail, options);
        return Event.dispatchExternalEvent(element, target, ev);
    }
    static triggerBackendEvent(element, name, detail, options, target) {
        return target
            ? Event.triggerExternalEvent(element, target, name, detail, options)
            : Event.triggerEvent(element, name, detail, options);
    }
}
//# sourceMappingURL=event.js.map